import React, { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";

import { useIntl } from "react-intl";

import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import User from "@mapmycustomers/shared/types/User";

import CreatorFilter from "@app/component/CreatorFilter/CreatorFilter";
import { getCurrentUserId } from "@app/store/iam";
import useAnalytics from "@app/util/contexts/useAnalytics";

import RoundButton from "../RoundButton";

import styles from "./ActionBar.module.scss";
import SortField from "./enum/SortField";
import ListDrawerOrderSelect from "./ListDrawerOrderSelect";

interface Props {
  creatorIds: User["id"][];
  onChangeCreators: (creatorIds: User["id"][]) => void;
  onChangeSortField: (field: SortField) => void;
  onChangeSortOrder: (order: SortOrder) => void;
  sort: {
    field: SortField;
    order: SortOrder;
  };
}

const ActionBar: React.FC<Props> = ({
  creatorIds,
  onChangeCreators,
  onChangeSortField,
  onChangeSortOrder,
  sort,
}) => {
  const intl = useIntl();
  const analyticIssuer = useAnalytics();
  const currentUserId = useSelector(getCurrentUserId)!;

  const myFiltersApplied = useMemo(
    () => creatorIds.length === 1 && creatorIds[0] === currentUserId,
    [creatorIds, currentUserId]
  );

  const handleSort = useCallback(
    (field: SortField, order: SortOrder) => {
      analyticIssuer?.clicked(["Sort Filters"], { field, sort: order });
      onChangeSortOrder(order);
      onChangeSortField(field);
    },
    [analyticIssuer, onChangeSortField, onChangeSortOrder]
  );

  const handleApplyMyFilters = useCallback(() => {
    onChangeCreators(myFiltersApplied ? [] : [currentUserId]);
  }, [currentUserId, myFiltersApplied, onChangeCreators]);

  return (
    <div className={styles.container}>
      <div className={styles.filters}>
        <CreatorFilter onChange={onChangeCreators} selectedUserIds={creatorIds} />
        <RoundButton active={myFiltersApplied} onClick={handleApplyMyFilters}>
          {intl.formatMessage({
            id: "listDrawer.search.myFilters",
            defaultMessage: "My Filters",
            description: "My filters button",
          })}
        </RoundButton>
      </div>
      <ListDrawerOrderSelect onChange={handleSort} sort={sort} />
    </div>
  );
};

export default ActionBar;
